/* eslint-disable react/jsx-no-undef */
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow, Typography } from '@mui/material';
import {
  AUDITING__FIELD_NAME,
  AUDITING__FIELD_NEW_VALUE,
  AUDITING__FIELD_OLD_VALUE,
  AUDITING__USER_CREATED,
  AUDITING__USER_MODIFIED,
} from 'translations/constants';
import { parseUtcTimestamp } from 'utils/date-utils';
import { AuditModel } from './types';

// Log times always read in Brussels time, whatever the viewer's device zone or browser language.
const logDateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Brussels',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** UTC change date from the API (sent without a `Z`) → Brussels `DD/MM/YYYY HH:mm`. */
const formatDate = (d: Date | string) => {
  const date = d instanceof Date ? d : parseUtcTimestamp(d);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(logDateFormatter.formatToParts(date).map(p => [p.type, p.value]));
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
};

export const AuditRow: React.FC<{ audit: AuditModel }> = ({ audit }) => {
  const [t] = useTranslation();

  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{audit.author}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(audit.changeDate)}</TableCell>
      <TableCell sx={{ pr: 0 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          {audit.changes.length ? t(AUDITING__USER_MODIFIED) : t(AUDITING__USER_CREATED)}
        </Typography>
        {audit.changes.map(c => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {`${t(AUDITING__FIELD_NAME)} '${c.propertyName}': ${t(AUDITING__FIELD_OLD_VALUE)}: "${c.oldValue}" → ${t(
              AUDITING__FIELD_NEW_VALUE,
            )}: "${c.newValue}"`}
          </Typography>
        ))}
      </TableCell>
      <TableCell align="right" width={56} />
    </TableRow>
  );
};
